import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import './ACLManager.css';

const ACLManager = ({ entityType, entityId, onClose }) => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [aclData, setAclData] = useState({
    visibility_mode: 'INHERIT_ENTERPRISE',
    acls: []
  });
  const [usuarios, setUsuarios] = useState([]);
  const [roles, setRoles] = useState([]);
  const [newAcl, setNewAcl] = useState({
    subject_type: 'USER',
    subject_id: '',
    permission_level: 'VIEW'
  });

  useEffect(() => {
    carregarDados();
  }, [entityId]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const endpoint = entityType === 'condicionante' 
        ? `/api/acl/condicionante/${entityId}/acl`
        : `/api/acl/tarefa/${entityId}/acl`;
      
      const [aclRes, usersRes, rolesRes] = await Promise.all([
        api.get(endpoint),
        api.get('/api/usuarios'),
        api.get('/api/usuarios/roles')
      ]);
      
      setAclData(aclRes.data);
      
      // Buscar usuários dos empreendimentos
      const empreendimentos = await api.get('/api/empreendimentos');
      const usuariosPromises = empreendimentos.data.map(emp => 
        api.get(`/api/usuarios/empreendimento/${emp.id}`).catch(() => ({ data: [] }))
      );
      const usuariosRes = await Promise.all(usuariosPromises);
      const allUsers = usuariosRes.flatMap(res => res.data);
      setUsuarios(allUsers);
      setRoles(rolesRes.data);
    } catch (error) {
      console.error('Erro ao carregar ACL:', error);
      alert('Erro ao carregar configurações de ACL');
    } finally {
      setLoading(false);
    }
  };

  const handleSalvar = async () => {
    try {
      const endpoint = entityType === 'condicionante'
        ? `/api/acl/condicionante/${entityId}/acl`
        : `/api/acl/tarefa/${entityId}/acl`;
      
      const payload = entityType === 'condicionante'
        ? { visibility_mode: aclData.visibility_mode, acls: aclData.acls }
        : { inherit_condition_acl: aclData.inherit_condition_acl, acls: aclData.acls };
      
      await api.post(endpoint, payload);
      alert('ACL atualizado com sucesso!');
      onClose();
    } catch (error) {
      console.error('Erro ao salvar ACL:', error);
      alert('Erro ao salvar configurações de ACL');
    }
  };

  const handleAdicionarACL = () => {
    if (!newAcl.subject_id) {
      alert('Selecione um usuário ou role');
      return;
    }
    setAclData({
      ...aclData,
      acls: [...aclData.acls, { ...newAcl }]
    });
    setNewAcl({ subject_type: 'USER', subject_id: '', permission_level: 'VIEW' });
  };

  const handleRemoverACL = (index) => {
    setAclData({
      ...aclData,
      acls: aclData.acls.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  if (!hasPermission('condition', 'set_acl') && entityType === 'condicionante') {
    return <div className="error">Você não tem permissão para gerenciar ACL.</div>;
  }

  return (
    <div className="acl-manager">
      <h3>Gerenciar ACL - {entityType === 'condicionante' ? 'Condicionante' : 'Tarefa'}</h3>
      
      {entityType === 'condicionante' ? (
        <div className="form-group">
          <label>Modo de Visibilidade</label>
          <select
            value={aclData.visibility_mode}
            onChange={(e) => setAclData({ ...aclData, visibility_mode: e.target.value, acls: e.target.value === 'INHERIT_ENTERPRISE' ? [] : aclData.acls })}
          >
            <option value="INHERIT_ENTERPRISE">Herdar do Empreendimento</option>
            <option value="RESTRICTED">Restrita (ACL específica)</option>
          </select>
        </div>
      ) : (
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={!aclData.inherit_condition_acl}
              onChange={(e) => setAclData({ ...aclData, inherit_condition_acl: !e.target.checked, acls: e.target.checked ? [] : aclData.acls })}
            />
            {' '}Usar ACL própria (não herdar da condicionante)
          </label>
        </div>
      )}

      {(aclData.visibility_mode === 'RESTRICTED' || !aclData.inherit_condition_acl) && (
        <>
          <div className="acl-list">
            <h4>ACLs Configuradas</h4>
            {aclData.acls.length === 0 ? (
              <p className="text-muted">Nenhuma ACL configurada</p>
            ) : (
              <table className="acl-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Subject</th>
                    <th>Nível</th>
                    <th>Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {aclData.acls.map((acl, index) => {
                    const subject = acl.subject_type === 'USER'
                      ? usuarios.find(u => u.id === acl.subject_id)
                      : roles.find(r => r.id === acl.subject_id);
                    return (
                      <tr key={index}>
                        <td>{acl.subject_type}</td>
                        <td>{subject?.nome || subject?.descricao || acl.subject_id}</td>
                        <td>{acl.permission_level}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoverACL(index)}
                          >
                            Remover
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="add-acl-form">
            <h4>Adicionar ACL</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Tipo</label>
                <select
                  value={newAcl.subject_type}
                  onChange={(e) => setNewAcl({ ...newAcl, subject_type: e.target.value, subject_id: '' })}
                >
                  <option value="USER">Usuário</option>
                  <option value="ROLE">Role</option>
                </select>
              </div>
              <div className="form-group">
                <label>Subject</label>
                <select
                  value={newAcl.subject_id}
                  onChange={(e) => setNewAcl({ ...newAcl, subject_id: e.target.value })}
                >
                  <option value="">Selecione...</option>
                  {newAcl.subject_type === 'USER' ? (
                    usuarios.map(usr => (
                      <option key={usr.id} value={usr.id}>
                        {usr.nome} ({usr.email})
                      </option>
                    ))
                  ) : (
                    roles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.descricao}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Nível</label>
                <select
                  value={newAcl.permission_level}
                  onChange={(e) => setNewAcl({ ...newAcl, permission_level: e.target.value })}
                >
                  <option value="VIEW">Ver</option>
                  <option value="EDIT">Editar</option>
                  <option value="EXECUTE">Executar</option>
                  <option value="APPROVE">Aprovar</option>
                </select>
              </div>
              <div className="form-group">
                <label>&nbsp;</label>
                <button type="button" className="btn btn-primary" onClick={handleAdicionarACL}>
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSalvar}>
          Salvar
        </button>
      </div>
    </div>
  );
};

export default ACLManager;
